import type { ReactNode } from 'react'
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox'
import { Field as BaseField } from '@base-ui/react/field'
import { Fieldset as BaseFieldset } from '@base-ui/react/fieldset'
import { Radio as BaseRadio } from '@base-ui/react/radio'
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group'
import { Switch as BaseSwitch } from '@base-ui/react/switch'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LabelRow, type FieldLabelProps, type LabelAccessoryProps } from './Field'
import './choice.css'

type ChoiceText = {
  /** Visible label. The whole row, label included, toggles the control. */
  label: ReactNode
  /** Supporting detail shown under the label and read as its description. */
  description?: ReactNode
  className?: string
}

export type CheckboxProps = ChoiceText & LabelAccessoryProps & Omit<BaseCheckbox.Root.Props, 'className' | 'children' | 'render'>

/** An independent yes or no choice, such as agreeing to terms or showing hidden cameras. */
export function Checkbox({ label, labelAccessory, description, className, name, disabled, indeterminate, ...props }: CheckboxProps) {
  return (
    <BaseField.Root name={name} disabled={disabled} className={cn('play-choice', className)}>
      <LabelRow block="play-choice" accessory={labelAccessory}>
        <BaseField.Label className="play-choice__label">
          <BaseCheckbox.Root className="play-checkbox" indeterminate={indeterminate} {...props}>
            <BaseCheckbox.Indicator className="play-checkbox__indicator">
              {indeterminate ? <Minus aria-hidden="true" /> : <Check aria-hidden="true" />}
            </BaseCheckbox.Indicator>
          </BaseCheckbox.Root>
          <span>{label}</span>
        </BaseField.Label>
      </LabelRow>
      {description && <BaseField.Description className="play-choice__description">{description}</BaseField.Description>}
    </BaseField.Root>
  )
}

export type SwitchProps = ChoiceText & FieldLabelProps & LabelAccessoryProps & Omit<BaseSwitch.Root.Props, 'className' | 'children' | 'render'>

/** A setting that takes effect immediately, such as muting previews. Use a checkbox for choices submitted with a form.
 *  `hideLabel` keeps the label as the switch's name but out of view, for a row that already names it, such as a
 *  table row or a card whose title says what the switch turns on. */
export function Switch({ label, hideLabel, labelAccessory, description, className, name, disabled, ...props }: SwitchProps) {
  return (
    <BaseField.Root name={name} disabled={disabled} className={cn('play-choice', className)}>
      <LabelRow block="play-choice" accessory={labelAccessory}>
        <BaseField.Label className="play-choice__label">
          <BaseSwitch.Root className="play-switch" {...props}>
            <BaseSwitch.Thumb className="play-switch__thumb" />
          </BaseSwitch.Root>
          <span className={hideLabel ? 'sr-only' : undefined}>{label}</span>
        </BaseField.Label>
      </LabelRow>
      {description && <BaseField.Description className="play-choice__description play-choice__description--switch">{description}</BaseField.Description>}
    </BaseField.Root>
  )
}

export type RadioGroupProps<Value extends string> = {
  /** The question the options answer. Rendered as the group’s legend. */
  label: ReactNode
  description?: ReactNode
  name?: string
  value?: Value
  defaultValue?: Value
  onValueChange?: (value: Value) => void
  disabled?: boolean
  required?: boolean
  className?: string
  children: ReactNode
}

/** One choice from a short list that stays visible. Use Select when the list is long. */
export function RadioGroup<Value extends string>({ label, description, name, onValueChange, className, children, ...props }: RadioGroupProps<Value>) {
  return (
    <BaseField.Root name={name} className={cn('play-form-field', className)}>
      <BaseFieldset.Root
        className="play-choice-group"
        render={<BaseRadioGroup {...props} onValueChange={(value) => onValueChange?.(value as Value)} />}
      >
        <BaseFieldset.Legend className="play-form-field__label">{label}</BaseFieldset.Legend>
        {description && <BaseField.Description className="play-form-field__description">{description}</BaseField.Description>}
        <div className="play-choice-group__items">{children}</div>
      </BaseFieldset.Root>
    </BaseField.Root>
  )
}

export type RadioProps = ChoiceText & { value: string; disabled?: boolean }

export function Radio({ value, label, description, disabled, className }: RadioProps) {
  return (
    <BaseField.Item disabled={disabled} className={cn('play-choice', className)}>
      <BaseField.Label className="play-choice__label">
        <BaseRadio.Root value={value} className="play-radio">
          <BaseRadio.Indicator className="play-radio__indicator" />
        </BaseRadio.Root>
        <span>{label}</span>
      </BaseField.Label>
      {description && <BaseField.Description className="play-choice__description">{description}</BaseField.Description>}
    </BaseField.Item>
  )
}
